/**
 * Dashboard da revenda (H11), servido da D07.
 *
 * ── A armadilha da task, e o que dá para cumprir dela ─────────────────────
 *
 * *"Número sem detalhamento. O gestor não consegue verificar, não confia, e
 * volta para a planilha — que é o que o produto deveria substituir."*
 *
 * O detalhamento existe onde ele leva ao MESMO conjunto que formou o número, e
 * só aí. Isso é menos do que a task pede, e a diferença tem uma causa concreta:
 * **nenhuma rota de lista aceita faixa de datas.** A Inbox filtra por canal,
 * situação e responsável; o board do funil, por responsável e risco. Nenhuma
 * das duas por período.
 *
 * Então:
 *
 *   Funil por etapa → board      EXATO. `em_aberto` é a foto de agora, e o
 *                                board mostra as oportunidades de agora. Mesmo
 *                                conjunto.
 *   Canal → Inbox filtrada       APROXIMADO, e a tela DIZ isso. Leva às
 *                                conversas daquele canal que estão na fila
 *                                agora, que não são as do período.
 *   Volume por dia               NÃO TEM. Nenhum link, em vez de um link que
 *                                mente.
 *
 * Um detalhamento que cai num conjunto diferente é pior que nenhum: o gestor
 * clica em "40 conversas", conta 12, e passa a não confiar em nada da tela. O
 * caminho para fechar isso é filtro por data na D02 e na D04, não aqui — a H11
 * declara "Endpoint → D07" no Fora.
 *
 * ── A conversão veio quando ganhou fonte; o SLA ainda não ──────────────────
 *
 * Os dois estavam no "Dentro" da task e nenhum tinha fonte, o que a D07 apurou
 * contra o schema: a C06 registrou que o Twenty não marca ganho nem perda, e a
 * regra de SLA é a D08.
 *
 * A conversão ganhou fonte em 24/09/2026: a revenda diz quais etapas finais são
 * ganho e quais são perda (057), e a perda diz o motivo (058). O bloco
 * "Resultado" mostra os dois números que a formam, e diz quando há etapa final
 * sem classificação, em vez de esconder a falta atrás de uma porcentagem.
 *
 * O SLA continua sem regra, e a tela continua mostrando só o fato: quanto o
 * cliente esperou pela primeira resposta.
 *
 * ── Cada bloco diz de quem são os números ─────────────────────────────────
 *
 * A resposta carrega `abrangencia` por bloco (D07): o funil se recorta pela
 * carteira de quem pergunta, o volume de atendimento não. Mostrar isso é o que
 * cumpre "filtros respeitando o escopo" sem fingir um recorte que não houve.
 */

import { useState, type ReactNode } from 'react';
import type { Dashboard, SaudeDaIntegracao } from '@cliqcrm/contracts';
import { useDashboard } from '@/shared/api/consultas';
import { Defasado, Estado } from '@/shared/ui/estados/estados';
import { idadeEmPalavras, instantePorExtenso } from '@/shared/ui/estados/idade';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import {
  diaCurto,
  diaPorExtenso,
  diasDoPeriodo,
  espera,
  PERIODOS,
  PERIODO_PADRAO,
  valorCurto,
  type ChaveDePeriodo,
} from '../lib/periodo';
import './painel.css';
import { Botao, CabecalhoDeTela, Cartao, Ficha, Segmentado, Selo, Tabela } from '@/shared/ui';
import { nomeDoProvedor } from '@/entities/integracoes/provedor';
import type { TomSelo } from '@/shared/ui';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { BotaoExportar } from './exportar';
import { nomeDaPlanilha, planilhaDaVisaoGeral } from '../lib/planilhas';

/** O escopo em palavras de quem lê, não no vocabulário da matriz. */
const ABRANGENCIA: Record<string, string> = {
  tenant: 'toda a organização',
  unidade: 'sua unidade',
  time: 'seu time',
  propria: 'sua carteira',
};

export function Painel({
  aoAbrirCanal,
  aoAbrirFunil,
}: {
  /** Leva à Inbox filtrada por canal. Ver a nota sobre detalhamento acima. */
  aoAbrirCanal: (canal: string) => void;
  aoAbrirFunil: () => void;
}): ReactNode {
  const [periodo, definirPeriodo] = useState<ChaveDePeriodo>(PERIODO_PADRAO);

  /*
   * Quantos dias, e não quais: quem sabe onde o dia começa é o fuso da
   * organização, no servidor. As datas eram calculadas aqui pelo relógio UTC,
   * e das 21h à meia-noite de Brasília "7 dias" viravam 6. As datas que valeram
   * voltam na resposta, e é delas que o cabeçalho fala.
   */
  const consulta = useDashboard({ dias: String(diasDoPeriodo(periodo)) });

  return (
    <div className="painel">
      <CabecalhoDeTela
        titulo="Visão geral"
        apoio="Como a organização está indo. Os números são do período escolhido e do que você alcança."
        {...(consulta.data
          ? {
              /*
                O período é FATO, e por isso `meta` e não `apoio`: ele diz de
                quando são os números, e não o que a tela faz. Era escrito em
                tom 3 aqui e em tom 2 em Tarefas, e as duas chamavam aquilo de
                "a linha de baixo".
              */
              meta: (
                <span>
                  {diaPorExtenso(consulta.data.periodo.de)} a{' '}
                  {diaPorExtenso(consulta.data.periodo.ate)}
                </span>
              ),
            }
          : {})}
        acoes={
          <>
            <Segmentado<ChaveDePeriodo>
              como="grupo"
              rotulo="Período"
              opcoes={PERIODOS.map((opcao) => ({ valor: opcao.chave, rotulo: opcao.nome }))}
              escolhida={periodo}
              aoEscolher={definirPeriodo}
            />
            <BotaoExportar
              planilha={
                consulta.data
                  ? () => ({
                      conteudo: planilhaDaVisaoGeral(consulta.data!),
                      nome: nomeDaPlanilha('visao-geral', consulta.data!.periodo),
                    })
                  : null
              }
            />
          </>
        }
      />

      {/* O corpo rola; o cabeçalho fica — com ele, o seletor de período. */}
      <div className="painel__corpo">
        <Estado consulta={consulta} forma="cartoes" linhas={4}>
          {(dados) => (
            <>
              {dados.frescor.stale && (
                <Defasado
                  projetadoEm={dados.frescor.projetado_em}
                  origem="Estes indicadores"
                  aoAtualizar={() => void consulta.refetch()}
                />
              )}

              <Resumo dados={dados} />
              <PorCanal dados={dados} aoAbrirCanal={aoAbrirCanal} />
              <PorEtapa dados={dados} aoAbrirFunil={aoAbrirFunil} />
              <Resultado dados={dados} />
              {/*
                O bloco só existe quando a resposta o traz.

                Ele é `integracao × ler` — do administrador da organização —,
                dentro de uma tela liberada por `indicador × ler`, que o gestor
                tem. O servidor deixou de mandá-lo a quem não pode vê-lo, e
                aqui a ausência é lida como ausência: sem bloco, sem título e
                sem a frase que a lista vazia diria.
              */}
              {dados.integracoes && <Integracoes itens={dados.integracoes} />}
              <Rodape calculadoEm={dados.frescor.projetado_em} />
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}

/* ── Os números do topo ──────────────────────────────────────────────────── */

/**
 * Quatro números, e não trinta.
 *
 * A Técnica da task: *"poucos números, bem escolhidos. Dashboard com trinta
 * indicadores não é lido."* Os quatro respondem as quatro perguntas que o
 * gestor faz de manhã: entrou trabalho? demoramos a responder? alguém ficou
 * sem resposta? o comercial andou?
 */
function Resumo({ dados }: { dados: Dashboard }): ReactNode {
  const somar = (
    campo: 'conversas_iniciadas' | 'conversas_resolvidas' | 'conversas_sem_resposta',
  ): number => dados.atendimento.por_dia.reduce((total, dia) => total + dia[campo], 0);

  const criadas = dados.comercial.por_dia.reduce((t, d) => t + d.oportunidades_criadas, 0);
  const fechadas = dados.comercial.por_dia.reduce((t, d) => t + d.oportunidades_fechadas, 0);
  const valor = dados.comercial.por_dia.reduce((t, d) => t + d.valor_fechado_micros, 0);

  // Mediana das medianas diárias. Aproximação declarada: serve para comparar
  // períodos entre si, não para auditar um atendimento específico.
  const medianas = dados.atendimento.por_dia
    .map((d) => d.primeira_resposta_p50_segundos)
    .filter((s): s is number => s !== null)
    .sort((a, b) => a - b);
  const mediana = medianas.length > 0 ? (medianas[Math.floor(medianas.length / 2)] ?? null) : null;

  const semResposta = somar('conversas_sem_resposta');

  return (
    <section className="painel__secao">
      <div className="painel__cabecalho">
        <h2>Atendimento</h2>
        <Etiqueta abrangencia={dados.atendimento.abrangencia} />
      </div>

      <div className="numeros">
        {/*
          Atendimentos, e não conversas (25/09/2026): o cliente que volta a
          escrever numa conversa encerrada é um atendimento novo, e desde a
          #510 ele volta para a MESMA conversa. O campo do contrato manteve o
          nome; o que ele conta mudou.
        */}
        <Numero
          rotulo="Atendimentos"
          valor={String(somar('conversas_iniciadas'))}
          /*
            Encerradas como apoio, e não como um quinto número: são conjuntos de
            dias diferentes — uma conversa aberta segunda e encerrada quarta
            aparece nas duas — e lado a lado, do mesmo tamanho, alguém as
            subtrairia. Como apoio, elas informam sem convidar à conta errada.
          */
          apoio={`${somar('conversas_resolvidas')} encerrados no período`}
          serie={dados.atendimento.por_dia.map((d) => ({
            dia: d.dia,
            valor: d.conversas_iniciadas,
          }))}
        />
        <Numero
          rotulo="Primeira resposta"
          valor={espera(mediana) ?? '—'}
          apoio={mediana === null ? 'nenhuma conversa a medir' : 'mediana do período'}
        />
        <Numero
          rotulo="Ficaram sem resposta"
          valor={String(semResposta)}
          destaque={semResposta > 0 ? 'atencao' : undefined}
          apoio="o cliente escreveu e ninguém respondeu"
        />
        <Numero
          rotulo="Oportunidades criadas"
          valor={String(criadas)}
          apoio={`${fechadas} fechadas · ${valorCurto(valor)}`}
          serie={dados.comercial.por_dia.map((d) => ({
            dia: d.dia,
            valor: d.oportunidades_criadas,
          }))}
          abrangencia={dados.comercial.abrangencia}
        />
      </div>
    </section>
  );
}

function Numero({
  rotulo,
  valor,
  apoio,
  serie,
  destaque,
  abrangencia,
}: {
  rotulo: string;
  valor: string;
  apoio?: string;
  serie?: readonly { dia: string; valor: number }[];
  destaque?: 'atencao';
  abrangencia?: string;
}): ReactNode {
  return (
    <article className="numero">
      <h3 className="numero__rotulo">
        {rotulo}
        {abrangencia && <Etiqueta abrangencia={abrangencia} discreta />}
      </h3>
      <p className={`numero__valor${destaque ? ` numero__valor--${destaque}` : ''}`}>{valor}</p>
      {apoio && <p className="numero__apoio">{apoio}</p>}
      {serie && serie.length > 1 && <Faisca serie={serie} rotulo={rotulo} />}
    </article>
  );
}

/**
 * O gráfico de barras por dia.
 *
 * Em CSS, sem biblioteca: são barras de altura proporcional, e trazer um
 * pacote de gráfico para isso custaria mais de download do que a tela inteira.
 * Quando houver gráfico de verdade a comparar — e não há na §11 —, a decisão
 * se refaz com o caso na mão.
 *
 * `role="img"` com `aria-label` porque a série é UMA informação, não trinta:
 * quem usa leitor de tela quer "subiu no fim do período", não trinta números
 * lidos em sequência. O total exato está no número grande, logo acima.
 */
function Faisca({
  serie,
  rotulo,
}: {
  serie: readonly { dia: string; valor: number }[];
  rotulo: string;
}): ReactNode {
  const maior = Math.max(...serie.map((p) => p.valor), 1);
  const primeiro = serie[0];
  const ultimo = serie[serie.length - 1];

  return (
    <div
      className="faisca"
      role="img"
      aria-label={`${rotulo} por dia, de ${diaCurto(primeiro?.dia ?? '')} a ${diaCurto(
        ultimo?.dia ?? '',
      )}. Maior valor no período: ${maior}.`}
    >
      {serie.map((ponto) => (
        <span
          key={ponto.dia}
          className="faisca__barra"
          // A barra tem altura mínima visível mesmo em zero: uma coluna
          // ausente e uma coluna zerada se parecem, e são coisas diferentes.
          style={{ height: `${Math.max((ponto.valor / maior) * 100, 4)}%` }}
          title={`${diaPorExtenso(ponto.dia)}: ${ponto.valor}`}
        />
      ))}
    </div>
  );
}

/* ── Por canal ───────────────────────────────────────────────────────────── */

function PorCanal({
  dados,
  aoAbrirCanal,
}: {
  dados: Dashboard;
  aoAbrirCanal: (canal: string) => void;
}): ReactNode {
  const linhas = dados.atendimento.por_canal;
  if (linhas.length === 0) return null;

  return (
    <section className="painel__secao">
      <div className="painel__cabecalho">
        <h2>Por canal</h2>
        <Etiqueta abrangencia={dados.atendimento.abrangencia} />
      </div>

      <Tabela
        rotulo="Atendimento por canal"
        colunas={[
          { chave: 'canal', rotulo: 'Canal' },
          { chave: 'conversas', rotulo: 'Atendimentos', numero: true },
          { chave: 'recebidas', rotulo: 'Recebidas', numero: true },
          { chave: 'enviadas', rotulo: 'Enviadas', numero: true },
          { chave: 'resposta', rotulo: '1ª resposta', numero: true },
          { chave: 'abrir', rotulo: 'Abrir na fila', rotuloOculto: true },
        ]}
        linhas={linhas.map((linha) => ({
          canal: nomeDoCanal(linha.canal) ?? linha.canal,
          conversas: linha.conversas_iniciadas,
          recebidas: linha.mensagens_recebidas,
          enviadas: linha.mensagens_enviadas,
          resposta: espera(linha.primeira_resposta_p50_segundos) ?? '—',
          abrir: (
            /*
              O detalhamento aproximado. O título diz o que ele faz, com
              todas as letras: leva à fila de AGORA daquele canal, não às
              conversas do período — a Inbox não filtra por data.
            */
            <Botao
              miudo
              title="Abre a fila de conversas deste canal. A fila mostra o estado de agora, não o período selecionado."
              onClick={() => aoAbrirCanal(linha.canal)}
            >
              Ver na fila
            </Botao>
          ),
        }))}
        chaveDe={(_, i) => linhas[i]!.canal}
      />
    </section>
  );
}

/* ── Funil ───────────────────────────────────────────────────────────────── */

/**
 * Fluxo e foto lado a lado, com nomes que não se confundem.
 *
 * "Entraram" é o fluxo do período; "em aberto" é a foto de agora. São perguntas
 * diferentes e o erro clássico é somar as duas — a D07 as guardou em colunas
 * separadas justamente por isso, e a tela mantém a separação no rótulo.
 *
 * ── Um quadro por funil, na ordem do quadro (25/09/2026) ──────────────────
 *
 * Era uma tabela só, com o valor cru da etapa em ordem alfabética: etapas de
 * funis diferentes misturadas, `ETAPA_DO_MEIO` no lugar do nome, e duas "Novo"
 * sem como saber de qual funil. A resposta agora traz o funil e o nome, na
 * ordem do quadro, e a tela só agrupa.
 *
 * ── Etapa final não está "em aberto" ──────────────────────────────────────
 *
 * Ali a venda já saiu do funil. Mostrar quantas estão nela como "em aberto", e
 * somar o valor delas ao "valor em aberto", dizia que a venda ganha ainda
 * estava por fechar. O "Entraram" continua: quantas chegaram ao fim no
 * período é pergunta legítima. Ganhas e perdidas moram no bloco Resultado.
 */
function PorEtapa({
  dados,
  aoAbrirFunil,
}: {
  dados: Dashboard;
  aoAbrirFunil: () => void;
}): ReactNode {
  const linhas = dados.funil.por_etapa;
  if (linhas.length === 0) return null;

  // Agrupa preservando a ordem da resposta, que já é a do quadro.
  const funis: { chave: string; nome: string; linhas: typeof linhas }[] = [];
  for (const linha of linhas) {
    const atual = funis.find((f) => f.chave === linha.funil);
    if (atual) (atual.linhas as (typeof linha)[]).push(linha);
    else funis.push({ chave: linha.funil, nome: linha.funil_nome, linhas: [linha] });
  }
  const variosFunis = funis.length > 1;

  return (
    <section className="painel__secao">
      <div className="painel__cabecalho">
        <h2>Funil</h2>
        <Etiqueta abrangencia={dados.funil.abrangencia} />
        <Botao onClick={aoAbrirFunil}>Abrir o funil</Botao>
      </div>

      {funis.map((funil) => (
        <div key={funil.chave} className="painel__funil">
          {variosFunis && <h3 className="painel__subtitulo">{funil.nome}</h3>}
          <Tabela
            rotulo={variosFunis ? `Funil ${funil.nome} por etapa` : 'Funil por etapa'}
            colunas={[
              { chave: 'etapa', rotulo: 'Etapa' },
              {
                chave: 'entradas',
                rotulo: 'Entraram',
                numero: true,
                titulo: 'Quantas entraram nesta etapa no período',
              },
              {
                chave: 'abertas',
                rotulo: 'Em aberto',
                numero: true,
                titulo: 'Quantas estão nesta etapa agora',
              },
              { chave: 'valor', rotulo: 'Valor em aberto', numero: true },
            ]}
            linhas={funil.linhas.map((linha) => ({
              etapa: linha.final ? (
                <>
                  {nomeDaEtapa(linha.nome)}{' '}
                  <span className="tabela__sem" title="A venda já saiu do funil nesta etapa">
                    (final)
                  </span>
                </>
              ) : (
                nomeDaEtapa(linha.nome)
              ),
              entradas: linha.entradas,
              /*
                Nulo é "não sei", e não zero: nenhum dia do período guardou a
                foto (D07). Na etapa final, "em aberto" não se aplica.
              */
              abertas:
                linha.final || linha.em_aberto === null ? (
                  <span
                    className="tabela__sem"
                    title={
                      linha.final
                        ? 'Etapa final: a venda já saiu do funil'
                        : 'O agregado deste período não guardou a foto'
                    }
                  >
                    —
                  </span>
                ) : (
                  linha.em_aberto
                ),
              valor:
                linha.final || linha.valor_em_aberto_micros === null
                  ? '—'
                  : valorCurto(linha.valor_em_aberto_micros),
            }))}
            chaveDe={(_, i) => funil.linhas[i]!.etapa}
          />
        </div>
      ))}
    </section>
  );
}

/* ── Resultado: ganhas, perdidas e por quê ───────────────────────────────── */

/**
 * A conversão, e os dois números que a formam (057, 058).
 *
 * A porcentagem vem ao lado de ganhas e perdidas, e não sozinha: "50%" de duas
 * oportunidades e "50%" de duzentas são leituras diferentes, e o gestor precisa
 * ver qual das duas tem na mão.
 *
 * Etapa final sem classificação não entra em lado nenhum, e a tela diz quantas
 * são. Uma conversão calculada só com parte das etapas, sem o aviso, seria o
 * número que ninguém consegue auditar que a D07 recusou.
 */
function Resultado({ dados }: { dados: Dashboard }): ReactNode {
  const { por_funil: funis, motivos_de_perda: motivos } = dados.resultado;
  const faltam = dados.resultado.etapas_finais_sem_resultado;
  if (funis.length === 0 && motivos.length === 0 && faltam === 0) return null;

  const totalPerdas = motivos.reduce((soma, m) => soma + m.quantidade, 0);

  return (
    <section className="painel__secao">
      <div className="painel__cabecalho">
        <h2>Resultado</h2>
        <Etiqueta abrangencia={dados.resultado.abrangencia} />
      </div>

      {faltam > 0 && (
        <p className="painel__nota">
          {faltam === 1
            ? '1 etapa final ainda não diz se é venda ganha ou perdida'
            : `${faltam} etapas finais ainda não dizem se são venda ganha ou perdida`}
          , e as oportunidades que entraram nelas não contam aqui. Classifique em Funil, Configurar
          etapas.
        </p>
      )}

      {funis.length === 0 ? (
        <p className="painel__nota">Nenhuma venda ganha ou perdida no período.</p>
      ) : (
        <Tabela
          rotulo="Ganhas e perdidas por funil"
          colunas={[
            { chave: 'funil', rotulo: 'Funil' },
            { chave: 'ganhas', rotulo: 'Ganhas', numero: true },
            { chave: 'perdidas', rotulo: 'Perdidas', numero: true },
            {
              chave: 'conversao',
              rotulo: 'Conversão',
              numero: true,
              titulo: 'Das que fecharam no período, quantas foram ganhas',
            },
            { chave: 'valor', rotulo: 'Valor ganho', numero: true },
          ]}
          linhas={funis.map((f) => ({
            funil: f.nome,
            ganhas: f.ganhas,
            perdidas: f.perdidas,
            conversao: porcentagem(f.ganhas, f.ganhas + f.perdidas),
            valor: valorCurto(f.valor_ganho_micros),
          }))}
          chaveDe={(_, i) => funis[i]!.funil}
        />
      )}

      {motivos.length > 0 && (
        <Tabela
          rotulo="Por que as vendas foram perdidas"
          colunas={[
            { chave: 'motivo', rotulo: 'Motivo da perda' },
            { chave: 'quantidade', rotulo: 'Vendas perdidas', numero: true },
            { chave: 'parte', rotulo: 'Das perdas', numero: true },
          ]}
          linhas={motivos.map((m) => ({
            motivo: m.motivo ?? 'Sem motivo informado',
            quantidade: m.quantidade,
            parte: porcentagem(m.quantidade, totalPerdas),
          }))}
          chaveDe={(_, i) => motivos[i]!.motivo ?? '(sem motivo)'}
        />
      )}
    </section>
  );
}

/** A parte sobre o todo, inteira. Sem todo, não há porcentagem: "—". */
function porcentagem(parte: number, todo: number): string {
  return todo === 0 ? '—' : `${Math.round((parte / todo) * 100)}%`;
}

/* ── Saúde das integrações ───────────────────────────────────────────────── */

/**
 * A parte da tela que é leitura VIVA (D07).
 *
 * A Técnica da task: *"quem percebe que o lead do Webmotors parou é o gestor
 * comercial, não o time de infraestrutura."* Por isso ela está no painel de
 * produto, e por isso não vem do agregado — um número de quinze minutos atrás
 * responderia a pergunta errada.
 *
 * ── O que ganha cor, e o que não ganha ────────────────────────────────────
 *
 * `status` e as contagens de falha ganham: são fatos objetivos, e evento na DLQ
 * significa que alguém desistiu de entregar. "Último evento há dois dias" NÃO
 * ganha, e a ausência é deliberada — não existe regra dizendo de quanto em
 * quanto tempo uma integração deve receber evento, e pintar de vermelho seria
 * inventar um limiar, que é o que a A07 proíbe. A tela mostra o fato e deixa a
 * leitura com quem conhece a revenda.
 */
function Integracoes({ itens }: { itens: readonly SaudeDaIntegracao[] }): ReactNode {
  return (
    <section className="painel__secao">
      <div className="painel__cabecalho">
        <h2>Integrações</h2>
      </div>

      {itens.length === 0 ? (
        <p className="painel__nota">
          Nenhuma integração ativa nesta organização. Sem elas, conversa e oportunidade não chegam.
        </p>
      ) : (
        <div className="integracoes">
          {itens.map((item) => (
            <Cartao como="article" variante="folgado" key={`${item.provider}-${item.kind}`}>
              <div className="cartao__linha">
                <b className="integracao__nome">{nomeDoProvedor(item.provider)}</b>
                <Selo tom={situacaoDaConexao(item.status)}>{item.status}</Selo>
              </div>

              <Ficha
                larguraRotulo="auto"
                direita
                linhas={[
                  {
                    rotulo: 'Último evento',
                    valor: idadeEmPalavras(item.ultimo_evento_em) ?? 'nenhum até agora',
                    titulo: instantePorExtenso(item.ultimo_evento_em) ?? undefined,
                  },
                  {
                    rotulo: 'Descoberta de schema',
                    valor: idadeEmPalavras(item.schema_sincronizado_em) ?? 'nunca rodou',
                    titulo: instantePorExtenso(item.schema_sincronizado_em) ?? undefined,
                  },
                ]}
              />

              {(item.eventos_em_dlq > 0 || item.eventos_em_retentativa > 0) && (
                <p className="integracao__falhas">
                  {item.eventos_em_dlq > 0 && (
                    <Selo tom="erro">{item.eventos_em_dlq} desistiram de entrar</Selo>
                  )}
                  {item.eventos_em_retentativa > 0 && (
                    <Selo tom="atencao">{item.eventos_em_retentativa} tentando de novo</Selo>
                  )}
                </p>
              )}
            </Cartao>
          ))}
        </div>
      )}
    </section>
  );
}

const situacaoDaConexao = (status: SaudeDaIntegracao['status']): TomSelo =>
  status === 'ativo' ? 'ok' : status === 'erro' ? 'erro' : 'atencao';

/* ── Rodapé ──────────────────────────────────────────────────────────────── */

/**
 * O que a tela NÃO afirma, escrito na tela.
 *
 * Não é humildade decorativa: a §11 lista conversão e SLA, e um gestor que
 * conhece a lista vai procurá-los. Sem esta nota ele conclui que a tela está
 * incompleta por descuido; com ela, sabe que a ausência é uma decisão e o que
 * precisaria existir para mudá-la.
 */
function Rodape({ calculadoEm }: { calculadoEm: string | null }): ReactNode {
  const idade = idadeEmPalavras(calculadoEm);

  return (
    <footer className="painel__rodape">
      <p className="painel__calculo">
        {idade ? (
          <>
            Calculado{' '}
            <time dateTime={calculadoEm ?? undefined} title={instantePorExtenso(calculadoEm) ?? ''}>
              {idade}
            </time>
            . Os números são atualizados por um processo em segundo plano, não a cada mensagem.
          </>
        ) : (
          <>Ainda não há agregado calculado para este período.</>
        )}
      </p>
    </footer>
  );
}

function Etiqueta({
  abrangencia,
  discreta,
}: {
  abrangencia: string;
  discreta?: boolean;
}): ReactNode {
  return (
    <span className={`abrangencia${discreta ? ' abrangencia--discreta' : ''}`}>
      {ABRANGENCIA[abrangencia] ?? abrangencia}
    </span>
  );
}
